<template>
    <div class="gallery-page">
        <div class="gallery-toolbar pb-2 d-flex align-items-center">
            <b-form-input
                v-model="filter"
                type="search"
                id="filterInput"
                class="gallery-search-input"
                placeholder="Поиск по объекту, смене или дате"
                autocomplete="off"
                debounce="1000"
            ></b-form-input>
            <b-button-group size="sm" class="gallery-status-filter ml-2" aria-label="Статус отчётов">
                <b-button
                    v-for="option in reportStatusOptions"
                    :key="option.value"
                    :variant="reportStatus === option.value ? option.variant : 'outline-secondary'"
                    @click="reportStatus = option.value"
                >
                    {{ option.text }}
                </b-button>
            </b-button-group>
            <b-button
                v-if="canManageGalleryAccess"
                variant="outline-secondary"
                class="ml-2 flex-shrink-0"
                @click="openAccessSettings"
                title="Настройки доступа"
            >
                <i class="fas fa-cog"></i>
            </b-button>
        </div>
        <gallery-work-filter v-model="workFilters" :report-status="reportStatus" />
        <p v-if="objectsCompleted && !objects.length && !galleryError" class="text-muted p-3" role="status">По заданным условиям фотографии не найдены</p>
        <div role="tablist" class="objects_container">
            <div
                v-for="(object, $index) in objects"
                :key="getObjectKey(object, $index)"
                class="gallery-object-item"
            >
                <b-button
                    variant="info"
                    class="w-100 gallery-object-toggle"
                    @click="onToggleClick($index)"
                    role="tab"
                >
                    <span class="gallery-object-title">{{object.building.name}}</span>
                    <i
                        class="fas gallery-object-chevron"
                        :class="isObjectVisible($index) ? 'fa-chevron-up' : 'fa-chevron-down'"
                    ></i>
                </b-button>
                <b-collapse :id="getObjectCollapseId(object, $index)"
                            :visible="isObjectVisible($index)"
                            accordion="my-accordion"
                            role="tabpanel"
                >
                    <object-images
                        :data="object"
                        :query="galleryQuery"
                        :report-status="reportStatus"
                        :work-filters="workFilters"
                        v-if="isObjectVisible($index)"
                    />
                </b-collapse>
            </div>
            <b-alert v-if="galleryError" show variant="danger" class="m-2" role="alert">
                {{ galleryError }}
                <b-button size="sm" variant="outline-danger" :disabled="objectsLoading" @click="retryObjects">Повторить</b-button>
            </b-alert>
            <infinite-loading :identifier="infiniteId" @infinite="getObjects">
                <template #no-results><span></span></template>
                <template #no-more><span></span></template>
                <template #error><span></span></template>
            </infinite-loading>
        </div>
        <b-modal
            v-model="accessSettingsVisible"
            title="Доступ к галерее"
            size="lg"
            ok-title="Сохранить"
            ok-variant="success"
            cancel-title="Отмена"
            :ok-disabled="accessSettingsLoading || objectAccessSaving"
            @ok="saveAccessSettings"
        >
            <b-alert v-if="accessSettingsError" show variant="danger">
                {{ accessSettingsError }}
            </b-alert>

            <b-form-checkbox v-model="accessConfig.enabled" switch class="mb-3">
                Доступ к галерее включен
            </b-form-checkbox>

            <b-tabs content-class="pt-3">
                <b-tab title="Все объекты" active>
                    <multiple-participants-config
                        v-model="accessConfig.full_access"
                        :users="participantUsers"
                        :departments="participantDepartments"
                        participant-label="получателя доступа"
                        :allowed-types="fullAccessTypes"
                        :show-fallback-option="false"
                        :show-priority="false"
                        empty-text="Доступ ко всем объектам не настроен"
                        hint-text="Эти участники видят все объекты в галерее"
                    />
                </b-tab>
                <b-tab title="По назначению">
                    <multiple-participants-config
                        v-model="accessConfig.object_access"
                        :users="participantUsers"
                        :departments="participantDepartments"
                        participant-label="объектной роли"
                        :show-object-roles="true"
                        :allowed-types="objectAccessTypes"
                        :object-role-options="galleryObjectRoleOptions"
                        :show-fallback-option="false"
                        :show-priority="false"
                        empty-text="Автоматический доступ по объектным ролям отключен"
                        hint-text="Эти роли видят только объекты, где они назначены"
                    />
                </b-tab>
                <b-tab title="Конкретные объекты">
                    <b-row>
                        <b-col cols="12" md="5" class="mb-3 mb-md-0">
                            <b-form-input
                                v-model="objectAccessSearch"
                                size="sm"
                                type="search"
                                placeholder="Поиск объекта"
                                autocomplete="off"
                            />
                            <div v-if="objectAccessLoading" class="text-center py-3">
                                <b-spinner small variant="secondary"/>
                            </div>
                            <div v-else class="gallery-object-access-list mt-2">
                                <b-button
                                    v-for="object in filteredObjectAccessObjects"
                                    :key="object.building_process_id"
                                    type="button"
                                    block
                                    size="sm"
                                    :variant="selectedObjectAccessId === object.building_process_id ? 'info' : 'outline-secondary'"
                                    class="text-left mb-2 gallery-object-access-button"
                                    @click="selectObjectAccess(object)"
                                >
                                    <div class="d-flex justify-content-between align-items-start">
                                        <span class="font-weight-bold gallery-object-access-name">
                                            {{ object.name }}
                                        </span>
                                        <i
                                            v-if="hasObjectAccessConfig(object)"
                                            class="fas fa-user-check ml-2"
                                            title="Доступ настроен"
                                        ></i>
                                    </div>
                                    <small class="d-block">
                                        Фото: {{ object.media_count || 0 }} ·
                                        {{ object.is_active ? 'В работе' : 'Архив' }}
                                    </small>
                                </b-button>
                                <div
                                    v-if="!filteredObjectAccessObjects.length"
                                    class="text-muted text-center py-3 border rounded"
                                >
                                    Объекты не найдены
                                </div>
                            </div>
                        </b-col>
                        <b-col cols="12" md="7">
                            <div v-if="selectedObjectAccessObject">
                                <div class="font-weight-bold mb-2">
                                    {{ selectedObjectAccessObject.name }}
                                </div>
                                <multiple-participants-config
                                    :value="selectedObjectAccessConfig"
                                    :users="participantUsers"
                                    :departments="participantDepartments"
                                    participant-label="получателя доступа"
                                    :show-object-roles="true"
                                    :allowed-types="specificObjectAccessTypes"
                                    :object-role-options="galleryObjectRoleOptions"
                                    :show-fallback-option="false"
                                    :show-priority="false"
                                    empty-text="Для этого объекта отдельный доступ не настроен"
                                    hint-text="Эти участники видят фотографии только выбранного объекта"
                                    @input="onSelectedObjectAccessConfigInput"
                                />
                                <div class="d-flex justify-content-end mt-2">
                                    <b-button
                                        size="sm"
                                        variant="success"
                                        :disabled="objectAccessSaving || !selectedObjectAccessDirty"
                                        @click="saveSelectedObjectAccess"
                                    >
                                        Сохранить доступ объекта
                                    </b-button>
                                </div>
                            </div>
                            <div v-else class="text-muted text-center py-3 border rounded">
                                Выберите объект слева
                            </div>
                        </b-col>
                    </b-row>
                </b-tab>
            </b-tabs>
        </b-modal>
    </div>
</template>

<script>
    import axios from 'axios';
    import {createGalleryImageLoader} from './galleryImageLoader';
    import GalleryWorkFilter from './GalleryWorkFilter.vue';
    import ObjectImages from "./ObjectImages/ObjectImages";
    import InfiniteLoading from 'vue-infinite-loading';
    import MultipleParticipantsConfig
        from "../Requests/RequestTypes/MultipleParticipantsConfig/MultipleParticipantsConfig.vue";

    function defaultAccessConfig() {
        return {
            enabled: true,
            full_access: { participants: [] },
            object_access: {
                participants: [
                    { type: 'object_role', roles: ['main_staff', 'curator', 'has_access'] }
                ]
            }
        };
    }

    function defaultObjectAccessConfig() {
        return { participants: [] };
    }

    function cloneJson(value) {
        return JSON.parse(JSON.stringify(value));
    }

    export default {
        name: "Gallery",
        provide() {
            this._imageLoader = createGalleryImageLoader();
            return {galleryImageLoader: this._imageLoader};
        },
        data() {
            return {
                limit: 20,
                page: 1,
                infiniteId: 0,
                objects: [],
                filter: null,
                workFilters: {workIds: [], groupIds: []},
                requestGeneration: 0,
                galleryError: '',
                objectsLoading: false,
                objectsCompleted: false,
                reportStatus: `all`,
                reportStatusOptions: [
                    {value: `all`, text: `Все`, variant: `primary`},
                    {value: `active`, text: `Действующие`, variant: `success`},
                    {value: `annulled`, text: `Аннулированные`, variant: `danger`}
                ],
                visible: this.isMobileViewport() ? [] : [0],
                accessSettingsVisible: false,
                accessSettingsLoading: false,
                accessSettingsError: null,
                accessConfig: defaultAccessConfig(),
                objectAccessObjects: [],
                objectAccessSearch: '',
                objectAccessLoading: false,
                objectAccessSaving: false,
                selectedObjectAccessId: null,
                selectedObjectAccessConfig: defaultObjectAccessConfig(),
                selectedObjectAccessDirty: false,
                fullAccessTypes: ['all', 'specific_users', 'department'],
                objectAccessTypes: ['object_role'],
                specificObjectAccessTypes: ['specific_users', 'department', 'object_role'],
                galleryObjectRoleOptions: [
                    { value: 'main_staff', text: 'Прораб' },
                    { value: 'curator', text: 'Куратор' },
                    { value: 'has_access', text: 'Имеющий доступ' }
                ]
            };
        },
        components: {
            ObjectImages,
            GalleryWorkFilter,
            InfiniteLoading,
            MultipleParticipantsConfig
        },
        computed: {
            isMobileGallery() {
                return Boolean(this.$store.state.isMobile) || this.isMobileViewport();
            },
            galleryQuery() {
                const query = String(this.filter || '').trim();
                return query || null;
            },
            galleryObjectLimit() {
                return this.isMobileGallery ? 8 : this.limit;
            },
            galleryPreviewShiftLimit() {
                return 0;
            },
            galleryPreviewMediaLimit() {
                return this.isMobileGallery ? 60 : 100;
            },
            currentUser() {
                return this.$store.getters.currentUser || {};
            },
            canManageGalleryAccess() {
                const roles = Array.isArray(this.currentUser.roles) ? this.currentUser.roles : [];
                return roles.includes('Admin') || roles.includes('Top');
            },
            participantUsers() {
                return (this.$store.state.users || [])
                    .filter(user => user && user.user_id)
                    .map(user => ({
                        value: user.user_id,
                        text: user.full_name || `${user.last_name || ''} ${user.first_name || ''}`.trim()
                    }));
            },
            participantDepartments() {
                return (this.$store.state.departments || [])
                    .filter(department => department && department.id)
                    .map(department => ({
                    value: department.id,
                    text: department.name
                }));
            },
            filteredObjectAccessObjects() {
                const query = String(this.objectAccessSearch || '').trim().toLowerCase();
                if (!query) {
                    return this.objectAccessObjects;
                }
                return this.objectAccessObjects.filter(object => {
                    return String(object.name || '').toLowerCase().includes(query);
                });
            },
            selectedObjectAccessObject() {
                return this.objectAccessObjects.find(object => {
                    return object.building_process_id === this.selectedObjectAccessId;
                }) || null;
            }
        },
        methods: {
            isMobileViewport() {
                return typeof window !== 'undefined' && window.innerWidth < 768;
            },
            getObjectId(object) {
                const building = object && object.building ? object.building : {};
                return building.processId || building.id || null;
            },
            getObjectKey(object, index) {
                return this.getObjectId(object) || index;
            },
            getObjectCollapseId(object, index) {
                return `collapse_${this.getObjectId(object) || index}`;
            },
            isObjectVisible(index) {
                return this.visible.includes(index);
            },
            getInitialVisibleObjects() {
                return this.isMobileGallery ? [] : [0];
            },
            resetObjects() {
                this.requestGeneration++;
                if (this._galleryRequest) this._galleryRequest.cancel();
                this.galleryError = '';
                this.objectsLoading = false;
                this.objectsCompleted = false;
                this.objects = [];
                this.page = 1;
                this.visible = this.getInitialVisibleObjects();
                this.infiniteId += 1;
            },
            appendObjects(items) {
                items.forEach(item => {
                    this.objects.push(item);
                });
            },
            retryObjects() {
                this.galleryError = '';
                this.infiniteId++;
            },
            async getObjects($state) {
                if (this.objectsLoading) return;
                const generation = this.requestGeneration;
                this.objectsLoading = true;
                const request = axios.CancelToken.source();
                this._galleryRequest = request;
                const limit = this.galleryObjectLimit;
                try {
                    const data = await this.$store.dispatch(`getImages`, {
                        cancelToken: request.token,
                        skip: limit * (this.page - 1),
                        limit,
                        query_building: this.galleryQuery,
                        shifts_limit: this.galleryPreviewShiftLimit,
                        media_limit: this.galleryPreviewMediaLimit,
                        report_status: this.reportStatus,
                        work_ids: this.workFilters.workIds.join(',') || undefined,
                        work_group_ids: this.workFilters.groupIds.join(',') || undefined
                    });
                    if (generation !== this.requestGeneration) return;
                    if (!data || !Array.isArray(data.items)) throw new Error('Не удалось загрузить галерею');
                    const items = data.items;
                    if (items.length) {
                        this.page += 1;
                        this.appendObjects(items);
                        if ($state) {
                            $state.loaded();
                        }
                    } else {
                        this.objectsCompleted = true;
                        if ($state) $state.complete();
                    }
                } catch (err) {
                    if (generation === this.requestGeneration) {
                        this.galleryError = 'Не удалось загрузить галерею. Попробуйте ещё раз.';
                        if ($state) $state.error();
                    }
                } finally {
                    if (generation === this.requestGeneration) this.objectsLoading = false;
                }
            },
            onToggleClick(index) {
                if (this.visible.includes(index)) {
                    this.visible = this.visible.filter(el => el !== index);
                } else if (this.isMobileGallery) {
                    this.visible = [index];
                } else {
                    this.visible.push(index);
                }
            },
            normalizeAccessConfig(config) {
                const source = config || {};
                return {
                    enabled: source.enabled !== false,
                    full_access: source.full_access || { participants: [] },
                    object_access: source.object_access || { participants: [] }
                };
            },
            normalizeObjectAccessConfig(config) {
                const source = config || {};
                return {
                    participants: Array.isArray(source.participants)
                        ? source.participants
                        : []
                };
            },
            hasObjectAccessConfig(object) {
                const config = object && object.gallery_access_config
                    ? object.gallery_access_config
                    : null;
                return Boolean(config && Array.isArray(config.participants) && config.participants.length);
            },
            async loadGalleryObjectAccess() {
                this.objectAccessLoading = true;
                try {
                    const data = await this.$store.dispatch(`getGalleryObjectAccess`);
                    this.objectAccessObjects = Array.isArray(data.items)
                        ? data.items.map(object => ({
                            ...object,
                            gallery_access_config: this.normalizeObjectAccessConfig(
                                object.gallery_access_config
                            )
                        }))
                        : [];

                    const selectedStillExists = this.objectAccessObjects.some(object => {
                        return object.building_process_id === this.selectedObjectAccessId;
                    });
                    if (!selectedStillExists) {
                        const firstObject = this.objectAccessObjects[0] || null;
                        await this.selectObjectAccess(firstObject, {skipSave: true});
                    } else if (!this.selectedObjectAccessDirty) {
                        const selected = this.selectedObjectAccessObject;
                        this.selectedObjectAccessConfig = cloneJson(
                            this.normalizeObjectAccessConfig(selected.gallery_access_config)
                        );
                    }
                } finally {
                    this.objectAccessLoading = false;
                }
            },
            async selectObjectAccess(object, options = {}) {
                if (!object) {
                    this.selectedObjectAccessId = null;
                    this.selectedObjectAccessConfig = defaultObjectAccessConfig();
                    this.selectedObjectAccessDirty = false;
                    return;
                }

                if (this.selectedObjectAccessDirty && !options.skipSave) {
                    await this.saveSelectedObjectAccess({silent: true});
                }

                this.selectedObjectAccessId = object.building_process_id;
                this.selectedObjectAccessConfig = cloneJson(
                    this.normalizeObjectAccessConfig(object.gallery_access_config)
                );
                this.selectedObjectAccessDirty = false;
            },
            onSelectedObjectAccessConfigInput(config) {
                this.selectedObjectAccessConfig = this.normalizeObjectAccessConfig(config);
                this.selectedObjectAccessDirty = true;
            },
            async saveSelectedObjectAccess() {
                const selected = this.selectedObjectAccessObject;
                if (!selected) {
                    return;
                }

                this.accessSettingsError = null;
                this.objectAccessSaving = true;
                try {
                    const data = await this.$store.dispatch(`updateGalleryObjectAccess`, {
                        id: selected.building_process_id,
                        accessConfig: this.selectedObjectAccessConfig
                    });
                    const savedConfig = this.normalizeObjectAccessConfig(data.access_config);
                    const index = this.objectAccessObjects.findIndex(object => {
                        return object.building_process_id === selected.building_process_id;
                    });
                    if (index !== -1) {
                        this.$set(this.objectAccessObjects, index, {
                            ...this.objectAccessObjects[index],
                            gallery_access_config: savedConfig
                        });
                    }
                    this.selectedObjectAccessConfig = cloneJson(savedConfig);
                    this.selectedObjectAccessDirty = false;
                } catch (err) {
                    this.accessSettingsError = err && err.response && err.response.data
                        ? err.response.data
                        : `Не удалось сохранить доступ объекта`;
                    throw err;
                } finally {
                    this.objectAccessSaving = false;
                }
            },
            async openAccessSettings() {
                this.accessSettingsError = null;
                this.accessSettingsVisible = true;
                this.accessSettingsLoading = true;
                try {
                    await Promise.all([
                        this.$store.dispatch(`getUsers`),
                        this.$store.dispatch(`getDepartments`)
                    ]);
                    const [data] = await Promise.all([
                        this.$store.dispatch(`getGalleryAccessSettings`),
                        this.loadGalleryObjectAccess()
                    ]);
                    this.accessConfig = this.normalizeAccessConfig(data.access_config);
                } catch (err) {
                    this.accessSettingsError = err && err.message
                        ? err.message
                        : `Не удалось загрузить настройки доступа`;
                } finally {
                    this.accessSettingsLoading = false;
                }
            },
            async saveAccessSettings(event) {
                event.preventDefault();
                this.accessSettingsError = null;
                this.accessSettingsLoading = true;
                try {
                    if (this.selectedObjectAccessDirty) {
                        await this.saveSelectedObjectAccess({silent: true});
                    }
                    const data = await this.$store.dispatch(
                        `updateGalleryAccessSettings`,
                        this.accessConfig
                    );
                    this.accessConfig = this.normalizeAccessConfig(data.access_config);
                    this.accessSettingsVisible = false;
                } catch (err) {
                    this.accessSettingsError = err && err.response && err.response.data
                        ? err.response.data
                        : `Не удалось сохранить настройки доступа`;
                } finally {
                    this.accessSettingsLoading = false;
                }
            }
        },
        beforeDestroy() {
            this.requestGeneration++;
            if (this._galleryRequest) this._galleryRequest.cancel();
            this._imageLoader.destroy();
        },
        watch: {
            workFilters: {deep: true, handler() { this.resetObjects(); }},
            filter() {
                this.resetObjects();
            },
            reportStatus() {
                this.resetObjects();
            }
        }
    }
</script>

<style scoped>

    .gallery-page {
        min-height: 0;
    }

    .gallery-toolbar {
        background: #fff;
        position: sticky;
        top: 0;
        z-index: 2;
    }

    .gallery-search-input {
        font-size: 16px;
    }

    .gallery-status-filter {
        flex: 0 0 auto;
    }

    .objects_container {
        overflow-y: auto;
        height: 85vh;
        -webkit-overflow-scrolling: touch;
    }

    .objects_container::-webkit-scrollbar {
        display: block;
        width: 20px;
    }

    .objects_container::-webkit-scrollbar-track {
        display: block;
        background: white;
        width: 20px;
    }

    .objects_container::-webkit-scrollbar-thumb {
        display: block;
        background-color: #17a2b8;
        border-radius: 20px;
        width: 20px;
    }

    .gallery-object-item {
        margin: 8px;
    }

    .gallery-object-toggle {
        align-items: center;
        display: flex;
        justify-content: space-between;
        min-height: 42px;
        white-space: normal;
        text-align: left;
    }

    .gallery-object-title {
        line-height: 1.2;
        min-width: 0;
        overflow-wrap: anywhere;
        padding-right: 8px;
    }

    .gallery-object-chevron {
        flex: 0 0 auto;
    }

    .gallery-object-access-list {
        max-height: 55vh;
        overflow-y: auto;
    }

    .gallery-object-access-button {
        white-space: normal;
    }

    .gallery-object-access-name {
        line-height: 1.2;
    }

    @media (max-width: 767.98px) {
        .gallery-page {
            margin-left: -8px;
            margin-right: -8px;
        }

        .gallery-toolbar {
            align-items: stretch !important;
            flex-wrap: wrap;
            gap: 6px;
            padding: 8px;
        }

        .gallery-search-input {
            flex: 1 1 100%;
        }

        .gallery-status-filter {
            margin-left: 0 !important;
        }

        .objects_container {
            height: calc(100vh - 112px);
            padding-bottom: 24px;
        }

        .objects_container::-webkit-scrollbar {
            width: 0;
        }

        .gallery-object-item {
            margin: 6px 8px;
        }

        .gallery-object-toggle {
            min-height: 46px;
            padding-left: 12px;
            padding-right: 12px;
        }
    }

</style>
